if (!customElements.get("m-localization-form")) {
  class MLocalizationForm extends HTMLElement {
    constructor() {
      super();
      const select = this.querySelector('[data-localization-select]');
      const form = select && select.closest('[data-localization-form]');
      const input = form && form.querySelector('input[data-localization-input]');

      const isLanguageSelector = input && input.name === 'locale_code';

      select && select.addEventListener('change', (e) => {
        const value = e.target.value;
        const form = select.closest('[data-localization-form]');
        const input = form.querySelector('input[data-localization-input]');
        input && input.setAttribute('value', value);

        // Persist selected language to localStorage so it survives checkout navigation
        if (isLanguageSelector) {
          try {
            localStorage.setItem('preferred-locale', value);
          } catch (err) {
            // localStorage may be unavailable in some contexts, ignore
          }
        }

        // Use Shopify's native localization form submission
        input && form.submit();
      });
    }
  }
  customElements.define('m-localization-form', MLocalizationForm);
}

/**
 * Language persistence after checkout and browser detection.
 *
 * Shopify's checkout or external payment gateways (like Paypal) may drop 
 * customers back at the default-locale URL (/), losing their chosen language.
 *
 * Fix:
 * 1. Discover if the navigation is internal (user actively browsing or using T-Lab switcher).
 * 2. If internal, always update localStorage so we learn their intent seamlessly.
 * 3. If external/returning, enforce their remembered language so they don't get reset to English.
 * 4. Detect their browser language forcefully on their very first visit to the site.
 */
(function () {
  try {
    const currentLang = document.documentElement.lang
      ? document.documentElement.lang.split('-')[0].toLowerCase()
      : null;

    if (!currentLang) return; // Cannot operate without knowing the current page's language

    const referrer = document.referrer.toLowerCase();
    const host = window.location.host.toLowerCase();
    
    // Check if the user is cleanly navigating inside our storefront (not cart, not checkout)
    // We treat 'cart' and 'checkout' referrers as external so if they bounce back to root, we restore their language.
    const isInternalNavigation = referrer.includes(host) 
                                 && !referrer.includes('checkout') 
                                 && !referrer.includes('cart');

    let storedLocale = localStorage.getItem('preferred-locale');

    function redirectToLocale(targetLocale) {
      if (!targetLocale || targetLocale === currentLang) return;
      
      const currentPath = window.location.pathname;
      const pathRegex = /^\/([a-z]{2})(?:\/|$)/i;
      const pathMatch = currentPath.match(pathRegex);

      let newPath;
      if (pathMatch) {
         newPath = currentPath.replace(pathRegex, `/${targetLocale}/`);
      } else {
         newPath = `/${targetLocale}${currentPath.startsWith('/') ? currentPath : '/' + currentPath}`;
      }

      newPath = newPath.replace(/\/\//g, '/'); // cleanup double slashes

      if (newPath !== currentPath) {
        window.location.replace(newPath + window.location.search + window.location.hash);
      }
    }

    if (isInternalNavigation) {
      // The user clicked a link or the T-Lab language switcher internally.
      // We respect this new page as their active choice and memorize it.
      if (storedLocale !== currentLang) {
        localStorage.setItem('preferred-locale', currentLang);
      }
    } else {
      // The user is arriving from outside (Google, Checkout, typing URL, Shop App).
      if (storedLocale) {
        // Enforce their stored preference to prevent random resets to English
        if (storedLocale !== currentLang) {
          redirectToLocale(storedLocale);
        }
      } else {
        // Very first visit ever. Try detecting browser language.
        const path = window.location.pathname;
        const localeSegmentRegex = /^\/([a-z]{2})(?:\/|$)/i;
        const match = path.match(localeSegmentRegex);

        if (!match) {
          // They landed on the root default URL.
          const browserLang = navigator.language ? navigator.language.split('-')[0].toLowerCase() : null;
          
          // Use hreflang tags injected by Shopify SEO to know available languages securely
          const alternateLinks = document.querySelectorAll('link[rel="alternate"][hreflang]');
          const supportedLocales = Array.from(alternateLinks).map(link => link.getAttribute('hreflang').toLowerCase().split('-')[0]);
          const availableLocales = supportedLocales.length > 0 ? supportedLocales : ['en', 'nl', 'tr'];

          if (browserLang && availableLocales.includes(browserLang) && browserLang !== currentLang) {
            localStorage.setItem('preferred-locale', browserLang);
            redirectToLocale(browserLang);
            return;
          }
        }
        
        // If we didn't redirect via browser language, just accept the current path as preference
        localStorage.setItem('preferred-locale', currentLang);
      }
    }
  } catch (err) {
    // Silent fail
  }
})();